// src/redux/enhancementSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import {
  EnhancementData,
  EnhancementTableRecord,
  CaseEnhancementPayload,
  multipleCaseEnhancementRequest,
  CaseEnhancementProcedure,
} from "../interfaces/IEnhancementForCghs";
import { API_URL, PATIENT_APIS } from "../utils/constants";
import {
  fetchEnhancementDataService,
  fetchProcedures,
  fetchSpecialities,
  PostEnhancementData,
} from "../apis/EnhancementService";
import { getEchsProcedures } from "../apis/schemeService";

interface EnhancementState {
  data: EnhancementTableRecord[];
  loading: boolean;
  error: string | null;
  procedures: any;
  specialities: any;
  isLoading: boolean;
}

const initialState: EnhancementState = {
  data: [],
  loading: false,
  error: null,
  isLoading: false,
  procedures: [],
  specialities: [],
};

export const fetchEnhancements = createAsyncThunk(
  "enhancement/fetchEnhancements",
  async ({
    caseId,
    visitId,
  }: {
    caseId: string | number;
    visitId: string | number;
  }) => {
    const data = await fetchEnhancementDataService(caseId, visitId);
    return transformEnhancementData(data);
  }
);

export const submitCaseEnhancement = createAsyncThunk(
  "enhancement/submitCaseEnhancement",
  async (
    {
      caseId,
      visitId,
      portalMetaDataId,
      payload,
    }: {
      caseId: string | number;
      visitId: string | number;
      portalMetaDataId: string | number;
      payload: CaseEnhancementPayload;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await PostEnhancementData(
        caseId,
        visitId,
        portalMetaDataId,
        payload
      );
      return response.data;
    } catch (error) {
      // Handle error
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data || "An error occurred");
      }
      return rejectWithValue("An unexpected error occurred");
    }
  }
);

export const fetchProceduresForEnhancement = createAsyncThunk(
  "enhancement/fetchProcedures",
  async (data: any) => {
    const response = await fetchProcedures(data);
    return response;
  }
);

export const fetchEchsProcedure = createAsyncThunk(
  "enhancement/fetchEchsProcedure",
  async ({
    hospitalId,
    patientType,
    schemeId,
  }: {
    hospitalId: number;
    patientType: string;
    schemeId: number;
  }) => {
    const response = await getEchsProcedures(hospitalId, patientType, schemeId);
    return response;
  }
);

export const fetchSpecialitiesForEnhancement = createAsyncThunk(
  "enhancement/fetchSpecialities",
  async () => {
    const response = await fetchSpecialities();
    return response;
  }
);

const transformEnhancementData = (
  data: EnhancementData[] | null | undefined
): EnhancementTableRecord[] => {
  if (!data) {
    return [];
  }

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return "N/A";

    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const [day, month, year] = dateString.split("-"); // Assuming "12-10-2024" format
    return `${day} ${months[Number(month) - 1]}, ${year}`;
  };

  return data?.map((item) => ({
    key: item?.id?.toString() || "N/A",
    initiatedDate:
      formatDate(item?.multipleCaseEnhancementResponse?.initiatedDate) || "N/A",
    claimId: item?.claimId?.toString() || "N/A",
    procedure:
      item?.multipleCaseEnhancementResponse?.enhancementProceduresResponseList
        ?.map((proc: any) => proc?.procedure || "N/A")
        .join(", ") || "-",
    shift: item?.multipleCaseEnhancementResponse?.shift || "-",
    days: item?.multipleCaseEnhancementResponse?.noOfDays?.toString() || "-",
    requestedAmount: item?.requestedAmount
      ? `₹ ${item.requestedAmount}`
      : "N/A",
    approvedAmount: item?.approvedAmount ? `₹ ${item.approvedAmount}` : "N/A",
    dateOfApprovalRejection:
      formatDate(
        item?.multipleCaseEnhancementResponse?.dateOfApprovalRejection
      ) || "N/A",
    noOfDays:
      item?.multipleCaseEnhancementResponse?.noOfDays?.toString() || "0",
    status: item?.status ? "Approved" : "Pending",
    remarks: "N/A",
    stageStateDto: item?.stageStateDto,
  }));
};

const enhancementSlice = createSlice({
  name: "enhancement",
  initialState: {
    ...initialState, // spread existing initial state
    submissionStatus: "idle",
    submissionError: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnhancements.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEnhancements.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchEnhancements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch enhancements";
      })
      .addCase(submitCaseEnhancement.pending, (state) => {
        state.submissionStatus = "loading";
        state.submissionError = null;
      })
      .addCase(submitCaseEnhancement.fulfilled, (state, action) => {
        state.submissionStatus = "succeeded";
        // Optionally handle the response, e.g., update some state
      })
      .addCase(submitCaseEnhancement.rejected, (state, action) => {
        state.submissionStatus = "failed";
        state.submissionError = action.payload as any;
      })
      .addCase(fetchProceduresForEnhancement.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchProceduresForEnhancement.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.procedures = action.payload;
        }
      )
      .addCase(fetchProceduresForEnhancement.rejected, (state) => {
        state.isLoading = false;
      })

      //echs procedure dropdown
      .addCase(fetchEchsProcedure.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchEchsProcedure.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          if (action.payload) {
            state.procedures = action?.payload?.procedures;
          }
        }
      )
      .addCase(fetchEchsProcedure.rejected, (state) => {
        state.isLoading = false;
        state.error = "Unable to Fetch Echs Procedure";
      })

      .addCase(fetchSpecialitiesForEnhancement.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchSpecialitiesForEnhancement.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.specialities = action.payload;
        }
      )
      .addCase(fetchSpecialitiesForEnhancement.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

export default enhancementSlice.reducer;
